// Worker-baserad wrapper för OpenCV.js. All bibliotekskörning sker i en
// dedikerad Web Worker så main thread aldrig blockeras (OpenCV.js är ~10 MB
// och initialiseringen tar 3-15s — på main thread låser det hela fliken).

// Vites `?worker`-syntax bundlar workern som en separat chunk vid build.
import OpenCVWorker from './opencv-worker?worker';

let worker: Worker | null = null;
let nextMsgId = 1;
let initPromise: Promise<void> | null = null;
const pending = new Map<
  number,
  { resolve: (v: any) => void; reject: (e: Error) => void }
>();

function ensureWorker(): Worker {
  if (worker) return worker;
  const w = new OpenCVWorker();
  w.onmessage = (e: MessageEvent<any>) => {
    const { id, ok, error } = e.data ?? {};
    const handler = pending.get(id);
    if (!handler) return;
    pending.delete(id);
    if (ok) handler.resolve(e.data);
    else handler.reject(new Error(error || 'Worker error'));
  };
  w.onerror = (e: ErrorEvent) => {
    for (const [, h] of pending) h.reject(new Error(e.message || 'Worker crashed'));
    pending.clear();
    initPromise = null;
    w.terminate();
    worker = null;
  };
  worker = w;
  return w;
}

function send<T>(message: any, transferList: Transferable[] = []): Promise<T> {
  const w = ensureWorker();
  const id = nextMsgId++;
  return new Promise<T>((resolve, reject) => {
    pending.set(id, { resolve, reject });
    w.postMessage({ ...message, id }, transferList);
  });
}

/**
 * Initialisera OpenCV i workern. Returnerar samma promise om redan kallad.
 * Kallar `onProgress` för att upplysa UI medan vi väntar (ungefärlig %, för
 * att visa något — vi har ingen riktig progress-event från CDN-fetchen).
 */
export function loadOpenCV(onProgress?: (pct: number) => void): Promise<void> {
  if (initPromise) return initPromise;

  initPromise = (async () => {
    // Visa fake progress under tiden init-meddelandet processas
    let cancelled = false;
    let pct = 5;
    if (onProgress) {
      onProgress(pct);
      const tick = setInterval(() => {
        if (cancelled) {
          clearInterval(tick);
          return;
        }
        // Asymptotisk till 90%
        pct = Math.min(90, pct + Math.max(1, Math.round((90 - pct) * 0.08)));
        onProgress(pct);
      }, 400);
      try {
        await send({ type: 'init' });
        cancelled = true;
        clearInterval(tick);
        onProgress(100);
      } catch (err) {
        cancelled = true;
        clearInterval(tick);
        initPromise = null; // tillåt retry
        throw err;
      }
    } else {
      try {
        await send({ type: 'init' });
      } catch (err) {
        initPromise = null;
        throw err;
      }
    }
  })();

  return initPromise;
}

/**
 * Är OpenCV redan initialiserat? Synkron check.
 */
export function isOpenCVReady(): boolean {
  // Om initPromise existerar och har resolverat, betraktar vi det som ready.
  // För enkelhet: vi kollar pending — om inga init-meddelanden pågår och
  // initPromise är truthy, då är vi ready (eller failade och rensade).
  return initPromise !== null && !Array.from(pending.values()).length;
}

/**
 * Skicka en inpaint-job till workern. Tar ImageData för bild + mask,
 * returnerar ImageData för resultatet.
 */
export interface WorkerInpaintInput {
  imageData: ImageData;
  maskData: ImageData;
  inpaintRadius?: number;
  algorithm?: 'TELEA' | 'NS';
}
export interface WorkerInpaintResult {
  resultData: ImageData;
  elapsedMs: number;
}

export async function inpaintViaWorker(
  input: WorkerInpaintInput,
): Promise<WorkerInpaintResult> {
  // Skicka pixel-buffrarna som transferable för zero-copy
  const transferList: Transferable[] = [
    input.imageData.data.buffer,
    input.maskData.data.buffer,
  ];
  const reply = await send<{
    resultData: ImageData;
    elapsedMs: number;
  }>(
    {
      type: 'inpaint',
      imageData: input.imageData,
      maskData: input.maskData,
      inpaintRadius: input.inpaintRadius,
      algorithm: input.algorithm,
    },
    transferList,
  );
  return reply;
}
